import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { normalizeQuotationRooms, QuotationRoomOccupancy, summarizeQuotationRooms } from '../QuotationBuilder/quotationRoomOccupancy';
import '../../css/QuotationRoomsPicker.css';

const MAX_ROOMS = 8;
const MAX_ADULTS_PER_ROOM = 4;
const MAX_CHILDREN_PER_ROOM = 4;
const MAX_CHILD_AGE = 12;

type Props = {
  value?: QuotationRoomOccupancy[];
  onChange: (rooms: QuotationRoomOccupancy[]) => void;
};

const clampRooms = (rooms?: QuotationRoomOccupancy[] | null) => (
  normalizeQuotationRooms(rooms)
    .slice(0, MAX_ROOMS)
    .map((room) => ({
      adults: Math.min(MAX_ADULTS_PER_ROOM, Math.max(1, Number(room.adults || 0))),
      children: Math.min(MAX_CHILDREN_PER_ROOM, Math.max(0, Number(room.children || 0))),
      childrenAges: Array.from(
        { length: Math.min(MAX_CHILDREN_PER_ROOM, Math.max(0, Number(room.children || 0))) },
        (_, index) => {
          const age = room.childrenAges?.[index];
          return age === null || age === undefined ? null : Math.max(0, Math.min(MAX_CHILD_AGE, Number(age)));
        }
      ),
    }))
);

const childAgeOptions = Array.from({ length: MAX_CHILD_AGE + 1 }, (_, age) => age);

const RoomsTravellersDropdown: React.FC<Props> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState<QuotationRoomOccupancy[]>(() => clampRooms(value));
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    transform?: string;
  } | null>(null);

  useEffect(() => {
    setRooms(clampRooms(value));
  }, [value]);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom - 16;
      const spaceAbove = rect.top - 16;
      const openUpward = spaceBelow < 360 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(240, Math.min(openUpward ? spaceAbove : spaceBelow, viewportHeight - 32));
      setPosition({
        top: openUpward ? rect.top - 8 : rect.bottom + 8,
        left: rect.left,
        width: Math.max(320, rect.width),
        maxHeight,
        transform: openUpward ? 'translateY(-100%)' : undefined,
      });
    };
    if (open) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [open]);

  const summary = useMemo(() => summarizeQuotationRooms(rooms), [rooms]);
  const canAddRoom = rooms.length < MAX_ROOMS;

  const updateRoom = (index: number, field: keyof QuotationRoomOccupancy, nextValue: number) => {
    setRooms((current) => current.map((room, itemIndex) => {
      if (itemIndex !== index) return room;
      if (field === 'adults') return { ...room, adults: Math.min(MAX_ADULTS_PER_ROOM, Math.max(1, nextValue)) };
      const children = Math.min(MAX_CHILDREN_PER_ROOM, Math.max(0, nextValue));
      const childrenAges = Array.from(
        { length: children },
        (_, ageIndex) => {
          const age = room.childrenAges?.[ageIndex];
          return age === null || age === undefined ? null : Math.max(0, Math.min(MAX_CHILD_AGE, Number(age)));
        }
      );
      return { ...room, children, childrenAges };
    }));
  };

  const addRoom = () => {
    if (!canAddRoom) return;
    setRooms((current) => [...current, { adults: 1, children: 0, childrenAges: [] }]);
  };

  const removeRoom = (index: number) => {
    setRooms((current) => (current.length <= 1 ? current : current.filter((_, itemIndex) => itemIndex !== index)));
  };

  const updateChildAge = (roomIndex: number, childIndex: number, nextValue: number) => {
    setRooms((current) => current.map((room, itemIndex) => {
      if (itemIndex !== roomIndex) return room;
      const childrenAges = Array.from(
        { length: Math.min(MAX_CHILDREN_PER_ROOM, Math.max(0, Number(room.children || 0))) },
        (_, ageIndex) => {
          const age = room.childrenAges?.[ageIndex];
          return age === null || age === undefined ? null : Math.max(0, Math.min(MAX_CHILD_AGE, Number(age)));
        }
      );
      childrenAges[childIndex] = Math.max(0, Math.min(MAX_CHILD_AGE, nextValue));
      return { ...room, childrenAges };
    }));
  };

  const hasCompleteChildAges = rooms.every((room) => {
    if (room.children <= 0) return true;
    const ages = room.childrenAges ?? [];
    return Array.from({ length: room.children }, (_, index) => ages[index] !== null && ages[index] !== undefined).every(Boolean);
  });

  const handleDone = () => {
    onChange(clampRooms(rooms));
    setOpen(false);
  };

  return (
    <div className="quotation-rooms-picker">
      <button
        ref={triggerRef}
        type="button"
        className="quotation-rooms-picker__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="quotation-rooms-picker__eyebrow">Rooms and Travellers</span>
        <span className="quotation-rooms-picker__summary">{summary}</span>
      </button>

      {open && position && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className="quotation-rooms-picker__panel"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
            transform: position.transform,
          }}
        >
          <div className="quotation-rooms-picker__rooms">
            {rooms.map((room, index) => (
              <div key={`quotation-room-${index}`} className="quotation-rooms-picker__room">
                <div className="quotation-rooms-picker__room-head">
                  <h3>Room {index + 1}</h3>
                  <button
                    type="button"
                    className="quotation-rooms-picker__remove"
                    onClick={() => removeRoom(index)}
                    disabled={rooms.length <= 1}
                    aria-label={`Remove room ${index + 1}`}
                  >
                    ×
                  </button>
                </div>
                <div className="quotation-rooms-picker__grid">
                  <div className="quotation-rooms-picker__counter-field">
                    <span>Adults</span>
                    <div className="quotation-rooms-picker__counter">
                      <button type="button" onClick={() => updateRoom(index, 'adults', room.adults - 1)} disabled={room.adults <= 1}>−</button>
                      <strong>{room.adults}</strong>
                      <button
                        type="button"
                        onClick={() => updateRoom(index, 'adults', room.adults + 1)}
                        disabled={room.adults >= MAX_ADULTS_PER_ROOM}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="quotation-rooms-picker__counter-field">
                    <span>Children</span>
                    <div className="quotation-rooms-picker__counter">
                      <button type="button" onClick={() => updateRoom(index, 'children', room.children - 1)} disabled={room.children <= 0}>−</button>
                      <strong>{room.children}</strong>
                      <button
                        type="button"
                        onClick={() => updateRoom(index, 'children', room.children + 1)}
                        disabled={room.children >= MAX_CHILDREN_PER_ROOM}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                {room.children > 0 && (
                  <div className="quotation-rooms-picker__child-ages">
                    <span className="quotation-rooms-picker__child-ages-label">Child ages</span>
                    <div className="quotation-rooms-picker__child-ages-grid">
                      {Array.from({ length: room.children }, (_, childIndex) => (
                        <label key={`quotation-room-${index}-child-${childIndex}`} className="quotation-rooms-picker__age-select">
                          <span className="quotation-rooms-picker__sr-only">Child {childIndex + 1} age</span>
                          <select
                            value={room.childrenAges?.[childIndex] ?? ''}
                            onChange={(event) => updateChildAge(index, childIndex, Number(event.target.value))}
                          >
                            <option value="" disabled>
                              Select age
                            </option>
                            {childAgeOptions.map((age) => (
                              <option key={age} value={age}>
                                {age} yrs
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="quotation-rooms-picker__footer">
            <button type="button" className="quotation-rooms-picker__add" onClick={addRoom} disabled={!canAddRoom}>+ Add room</button>
            <button type="button" className="quotation-rooms-picker__done" onClick={handleDone} disabled={!hasCompleteChildAges}>Done</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RoomsTravellersDropdown;
